import abcLogo from "/abclabs-white.svg";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  PreJoin,
  RoomContext,
  VideoConference,
} from "@livekit/components-react";
import "@livekit/components-styles";
import "./App.css";
import { ErrorDialog } from "./components/ErrorDialog";
import { NotificationToast } from "./components/NotificationToast";
import {
  ExternalE2EEKeyProvider,
  Room,
  RoomEvent,
  type E2EEOptions,
} from "livekit-client";
import { CustomControlBar } from "./components/CustomControlBar.tsx";
import { LanguageButton } from "./components/LanguageButton.tsx";
import { useTranslation } from "react-i18next";
import { WaitRoom } from "./components/WaitRoom.tsx";

const LIVEKIT_SERVER_URL = import.meta.env.VITE_LIVEKIT_SERVER_URL as string;
if (!LIVEKIT_SERVER_URL) throw new Error("VITE_LIVEKIT_SERVER_URL is required");

const CAREOS_CONNECT_API_URL = import.meta.env
  .VITE_CAREOS_CONNECT_API_URL as string;
if (!CAREOS_CONNECT_API_URL)
  throw new Error("VITE_CAREOS_CONNECT_API_URL is required");

type SessionState =
  | "landingPage"
  | "idle"
  | "waitroom"
  | "prejoin"
  | "joining"
  | "active"
  | "error";

async function parseJsonSafe(resp: Response) {
  try {
    return await resp.json();
  } catch {
    return null;
  }
}

export default function App() {
  const [urlSid, setUrlSid] = useState<string | null>(null);
  const [accessCode, setUrlAccessCode] = useState<string | null>(null);

  useEffect(() => {
    const readHash = () => {
      const params = new URLSearchParams(window.location.hash.slice(1));
      const sidRaw = params.get("sid");
      const codeRaw = params.get("code");

      const sid = sidRaw && sidRaw.trim() ? sidRaw.trim() : null;
      const code = codeRaw && codeRaw.trim() ? codeRaw.trim() : null;

      setUrlSid(sid);
      setUrlAccessCode(code);

      if (sid) {
        setSessionState("prejoin");
      } else if (code) {
        window.sessionStorage.setItem("ac", code);
        setSessionState("waitroom");
      } else {
        setSessionState("landingPage");
      }
    };

    readHash();
    window.addEventListener("hashchange", readHash);
    return () => window.removeEventListener("hashchange", readHash);
  }, []);
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [sessionState, setSessionState] = useState<SessionState>("idle");
  const [rpcNotification, setRpcNotification] = useState<string>("");
  const [toastKey, setToastKey] = useState<number>(0);

  const [hasJoinedOnce, setHasJoinedOnce] = useState<boolean>(false);
  const lastAutoJoinedSidRef = useRef<string | null>(null);
  const pendingSidRef = useRef<string | null>(null);

  const [lkToken, setLkToken] = useState<string | null>(null);

  const keyProviderRef = useRef<ExternalE2EEKeyProvider | null>(null);
  if (!keyProviderRef.current) {
    keyProviderRef.current = new ExternalE2EEKeyProvider();
  }

  const startJoin = (sid: string) => {
    pendingSidRef.current = sid;
    setSessionState("joining");
  };

  useEffect(() => {
    if (sessionState !== "idle") return;
    if (!urlSid) return;
    const sidChanged = urlSid !== lastAutoJoinedSidRef.current;
    if (!hasJoinedOnce || sidChanged) {
      lastAutoJoinedSidRef.current = urlSid;
      startJoin(urlSid);
    }
  }, [sessionState, urlSid, hasJoinedOnce]);

  const handleJoin = () => {
    if (!urlSid) return;
    startJoin(urlSid);
  };

  const getStarted = () => {
    const ac = window.sessionStorage.getItem("ac");
    if (!ac) return;
    const params = new URLSearchParams(window.location.hash.slice(1));
    params.set("code", ac);
    window.location.hash = params.toString();
  };

  useEffect(
    () => {
      if (sessionState !== "joining") return;
      const pendingSid = pendingSidRef.current;
      if (!pendingSid) return;

      let cancelled = false;

      const authEndpoint = `${CAREOS_CONNECT_API_URL}/video/auth/livekit/workplace`;

      (async () => {
        try {
          const resp = await fetch(authEndpoint, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ sessionId: pendingSid }),
          });
          const data = await parseJsonSafe(resp);
          if (!resp.ok) {
            setErrorMessage(t("errors.tryAgainLater"));
            setSessionState("idle");
            return;
          }
          if (!data?.token) {
            setErrorMessage(t("errors.tryAgainLater"));
            setSessionState("idle");
            return;
          }
          if (!cancelled) {
            setLkToken(data.token as string);
          }
        } catch (err) {
          console.error(err);
          if (!cancelled) {
            setErrorMessage(t("errors.tryAgainLater"));
            setSessionState("idle");
          }
        }
      })();

      return () => {
        cancelled = true;
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sessionState]
  );

  const room = useMemo(() => {
    const keyProvider = keyProviderRef.current!;
    const e2ee: E2EEOptions = {
      keyProvider,
      worker: new Worker(
        new URL("livekit-client/e2ee-worker", import.meta.url)
      ),
    };

    const roomOptions = {
      e2ee,
    };
    return new Room(roomOptions);
  }, []);

  useEffect(() => {
    return () => {
      room.disconnect();
    };
  }, [room]);

  const [connected, setConnected] = useState(false);

  /** Called when the user presses "Join" in PreJoin */
  const handlePreJoinSubmit = useCallback(
    async (values: {
      username?: string;
      audioEnabled?: boolean;
      videoEnabled?: boolean;
      // these keys exist in components-react; we guard with optional chaining
      audioDeviceId?: string | null;
      videoDeviceId?: string | null;
      captureSettings?: {
        resolution?: { width: number; height: number; frameRate?: number };
      };
    }) => {
      if (!urlSid) {
        return;
      }
      setSessionState("joining");

      try {
        const resp = await fetch(
          `${CAREOS_CONNECT_API_URL}/video/auth/livekit/workplace`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ sessionId: urlSid }),
          }
        );
        const data = await resp.json().catch(() => null);
        if (!resp.ok || !data?.token) {
          throw new Error("Failed to get LiveKit token");
        }
        setLkToken(data.token);

        const e2eeKey = data.encryptionKey;
        const keyProvider = keyProviderRef.current;
        if (!keyProvider) {
          throw new Error("E2EE key provider not initialized");
        }

        await keyProvider.setKey(e2eeKey);
        await room.setE2EEEnabled(true);

        await room.connect(LIVEKIT_SERVER_URL, data.token, {
          autoSubscribe: true,
        });

        if (values.audioEnabled) {
          await room.localParticipant.setMicrophoneEnabled(true, {
            deviceId: values.audioDeviceId ?? "default",
          });
        } else {
          await room.localParticipant.setMicrophoneEnabled(false, {
            deviceId: values.audioDeviceId ?? "default",
          });
        }

        if (values.videoEnabled) {
          await room.localParticipant.setCameraEnabled(true, {
            deviceId: values.videoDeviceId ?? "default",
          });
        } else {
          await room.localParticipant.setCameraEnabled(false);
        }

        setConnected(true);
        setSessionState("active");
      } catch (e) {
        console.log(`Failed to connect: ${e}`);
        setSessionState("error");
      }
    },
    [urlSid, room]
  );

  useEffect(() => {
    const onDisconnected = () => {
      setHasJoinedOnce(true);
      setLkToken(null);
      setRpcNotification("");

      const ac = window.sessionStorage.getItem("ac");
      if (ac) {
        const params = new URLSearchParams(window.location.hash.slice(1));
        params.delete("sid");
        params.delete("code");
        window.location.hash = params.toString();
      } else {
        setSessionState("prejoin");
      }
    };

    room.off(RoomEvent.Disconnected, onDisconnected);
    room.on(RoomEvent.Disconnected, onDisconnected);

    return () => {
      room.off(RoomEvent.Disconnected, onDisconnected);
    };
  }, [room, urlSid]);

  useEffect(() => {
    const onConnected = () => {
      setRpcNotification("");
      setSessionState("active");
    };

    room.off(RoomEvent.Connected, onConnected);
    room.on(RoomEvent.Connected, onConnected);

    return () => {
      room.off(RoomEvent.Disconnected, onConnected);
    };
  }, [room, urlSid]);

  useEffect(() => {
    if (!connected) return;

    const handler = async ({ payload }: { payload?: string }) => {
      const text = String(payload ?? "");
      setRpcNotification(text);
      setToastKey((k) => k + 1);
      return "ok"; // reply so caller doesn't timeout
    };

    // Defensive: avoid duplicate registration during hot reloads
    try {
      room.unregisterRpcMethod?.("showNotification");
      // eslint-disable-next-line no-empty
    } catch {}

    room.registerRpcMethod("showNotification", handler);

    return () => {
      try {
        room.unregisterRpcMethod?.("showNotification");
        // eslint-disable-next-line no-empty
      } catch {}
    };
  }, [connected, room]);

  const hasReadySid = !!urlSid;

  let lkTheme = "default";
  const params = new URLSearchParams(window.location.search);
  const dark = params.has("dark");
  if (dark) {
    lkTheme = "abclabs-dark";
  }

  let background = "bg-gradient-to-br from-blue-900 to-pink-600/10";
  if (lkTheme === "abclabs-dark") {
    background = "bg-black";
  }

  return (
    <main className={`relative ${background}`} data-lk-theme="default">
      <div
        className="mx-auto w-full px-4 min-h-screen flex flex-col"
        data-lk-theme={lkTheme}
      >
        {sessionState !== "active" && (
          <header
            id="page-header"
            className="relative flex items-end justify-center mb-2 h-20"
          >
            {/* Left-aligned logo */}
            <img
              src={abcLogo}
              alt="Abc Labs Logo"
              className="h-20 w-20 absolute left-0 bottom-0"
            />

            {/* Centered title */}
            <h1 className="hidden md:inline text-5xl font-light text-white -translate-y-2">
              {t("header.title")}
            </h1>

            <LanguageButton />
          </header>
        )}
        {sessionState === "landingPage" &&
          window.sessionStorage.getItem("ac") && (
            <div className="card text-center mb-6 mt-3 landing-page">
              <div className="flex flex-col items-center gap-3">
                <button
                  className="flex items-center justify-center gap-2 py-2 px-4 rounded shadow get-started-button"
                  onClick={getStarted}
                  title={t("getStarted.buttonText")}
                >
                  <span>{t("getStarted.buttonText")}</span>
                </button>
              </div>
            </div>
          )}
        {sessionState === "idle" && (
          <div id="join-flow" className="card text-center mb-6 mt-3">
            <div className="flex flex-col items-center gap-3">
              <button
                className="flex items-center justify-center gap-2 bg-purple-900/80 hover:bg-purple-950 text-white font-thin py-2 px-4 rounded disabled:opacity-50 shadow text-lg"
                onClick={handleJoin}
                disabled={!hasReadySid}
                title={t("join.joinSession")}
              >
                <img src="/call_icon.svg" alt="" className="h-5 w-5" />
                <span>{t("join.joinSession")}</span>
              </button>
            </div>
          </div>
        )}
        {sessionState === "waitroom" && accessCode && (
          <WaitRoom
            accessCode={accessCode}
            apiBaseUrl={CAREOS_CONNECT_API_URL}
            onSessionFound={(sessionId) => {
              // When we discover a sessionId, turn the URL into a "sid" URL.
              // This leverages all existing sid-based logic (PreJoin, auto-join, etc).
              const params = new URLSearchParams(window.location.hash.slice(1));
              params.set("sid", sessionId);
              params.delete("code");
              window.location.hash = params.toString();
            }}
            onUnauthorized={() => {
              window.sessionStorage.removeItem("ac");
              setUrlAccessCode("");
            }}
          />
        )}
        {sessionState === "prejoin" && (
          <div className="grid place-items-center px-4 pb-10 h-full">
            <div className="w-full h-[calc(100vh-150px)]">
              <PreJoin
                onSubmit={handlePreJoinSubmit}
                defaults={{
                  audioEnabled: true,
                  videoEnabled: true,
                  username: "donor",
                }}
                joinLabel={t("join.joinSession")}
                micLabel={t("controls.microphone")}
                camLabel={t("controls.camera")}
              />
            </div>
          </div>
        )}
        {sessionState === "joining" && (
          <p className="text-center text-white font-thin text-sm mb-6">
            {t("join.connecting")}
          </p>
        )}
        {sessionState === "active" && connected && (
          <section
            id="session-section"
            className="w-full mt-8 flex flex-col items-center"
          >
            <div className="w-full h-[calc(100vh-150px)]">
              {lkToken ? (
                <RoomContext.Provider value={room}>
                  <div className="flex h-full flex-col">
                    <div className="flex-1 min-h-0 grid items-stretch gap-3 p-3">
                      <div className="abc-room min-w-0 min-h-0">
                        <VideoConference />
                      </div>
                    </div>

                    <div className="px-3 pb-3">
                      <CustomControlBar />
                    </div>
                  </div>
                </RoomContext.Provider>
              ) : (
                <div />
              )}
            </div>
          </section>
        )}
        <ErrorDialog
          message={errorMessage}
          onClose={useCallback(() => setErrorMessage(""), [])}
        />
        {sessionState === "active" && rpcNotification && (
          <NotificationToast
            key={toastKey}
            notification={rpcNotification}
            duration={15_000}
          />
        )}
      </div>
    </main>
  );
}
